import { print } from 'graphql/index';
import { type MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import FlashMessage from '~/shared/components/flash-message';
import { LOCAL_STORAGE_KEY } from '~/shared/components/subscription/constants';
import { mapCategoriesToOptions, normalizeCategoryIds } from '~/shared/components/subscription/helpers';
import type { SubscriptionCategoryItem } from '~/shared/components/subscription/helpers/subscription-categories-cache';
import {
  fetchSubscriptionCategories,
  getCachedSubscriptionCategories,
} from '~/shared/components/subscription/helpers/subscription-categories-cache';
import type { SubscriptionFormValues, SubscriptionMutationResponse } from '~/shared/components/subscription/types';
import { getSubscriptionFormConfig } from '~/shared/configs/forms';
import { createOneSubscriptionMutation } from '~/shared/graphql-queries/subscriptions';
import { fetchGraphQL } from '~/shared/helpers/fetch-graphql';
import { useTypedTranslation } from '~/shared/hooks';
import type { Locale } from '~/universal/constants';

type UseSubscriptionProps = Readonly<{
  lng: Locale;
}>;

const CATEGORIES_URL = '/api/graphql';

const useSubscription = ({ lng }: UseSubscriptionProps) => {
  const t = useTypedTranslation(['button', 'field', 'message', 'text', 'error']);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isSubscribedLocal, setIsSubscribedLocal] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
  });
  const [categories, setCategories] = useState<readonly SubscriptionCategoryItem[]>([]);

  const isCategoriesLoadedRef = useRef(false);

  const categoryOptions = useMemo(() => mapCategoriesToOptions(categories), [categories]);

  const formConfig = useMemo(() => {
    return getSubscriptionFormConfig({
      t,
      items: categoryOptions,
      isCategoriesLoading,
    });
  }, [categoryOptions, isCategoriesLoading, t]);

  const handleModalOpen = useCallback(async () => {
    setIsModalOpen(true);

    if (isCategoriesLoadedRef.current) {
      return;
    }

    const cachedCategories = getCachedSubscriptionCategories({
      lng,
      categoriesUrl: CATEGORIES_URL,
    });

    if (cachedCategories) {
      setCategories(cachedCategories);
      isCategoriesLoadedRef.current = true;
      return;
    }

    try {
      setIsCategoriesLoading(true);

      const nextCategories = await fetchSubscriptionCategories({
        lng,
        categoriesUrl: CATEGORIES_URL,
      });

      setCategories(nextCategories);
      isCategoriesLoadedRef.current = true;
    } catch {
      setCategories([]);
      isCategoriesLoadedRef.current = true;
    } finally {
      setIsCategoriesLoading(false);
    }
  }, [lng]);

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onModalContentClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleSubmit = useCallback(
    async ({ userName, email, categories: selectedCategories }: SubscriptionFormValues) => {
      const normalizedCategories = normalizeCategoryIds(selectedCategories);

      try {
        setIsSubmitting(true);

        const successNode = await fetchGraphQL<
          SubscriptionMutationResponse,
          {
            userName: string;
            email: string;
            categories: string[];
            type: string;
          },
          NonNullable<SubscriptionMutationResponse['createOneSubscriptionMutation']>
        >({
          lng,
          operationName: 'createOneSubscriptionMutation',
          query: print(createOneSubscriptionMutation),
          variables: {
            userName,
            email,
            categories: normalizedCategories,
            type: 'posts',
          },
          select: (data) => data.createOneSubscriptionMutation,
          emptyDataMessage: 'Empty GraphQL response',
        });

        if (successNode.message) {
          toast.success(
            <FlashMessage
              title={successNode.title || t('message.success.title')}
              message={successNode.message || t('message.success.message')}
            />,
          );
        }

        if (typeof window !== 'undefined') {
          window.localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
        }

        setIsSubscribedLocal(true);
        setIsModalOpen(false);
      } catch (error) {
        const message = error instanceof Error ? error.message : t('error.message.unknown-error.message');

        toast.error(<FlashMessage title={t('error.message.unknown-error.title')} message={message} />);

        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [lng, t],
  );

  return {
    isModalOpen,
    isSubmitting,
    isCategoriesLoading,
    handleModalOpen,
    isSubscribedLocal,
    onModalClose,
    onModalContentClick,
    handleSubmit,
    formConfig,
    t,
  };
};

export default useSubscription;
